// src/rec/formats.js — shapes, sizes and how this browser will make the file.
//
// The goal is an MP4 with H.264 video and AAC audio: the one file every
// social app accepts. Browsers get there differently, so ClippCut picks the
// best route this browser has, in this order:
//   1. WebCodecs + AAC  → we encode and write a standard MP4 ourselves
//                         (Chrome and Edge on Windows, Mac, Android, ChromeOS)
//   2. MediaRecorder MP4 → the browser writes the MP4 (Safari on iPhone, iPad
//                         and Mac; Chrome where AAC isn't available)
//   3. WebCodecs + Opus → a standard MP4 with Opus sound (Firefox)
//   4. MediaRecorder WebM → the last resort (older browsers). TikTok and
//                         YouTube take WebM; Instagram doesn't.

export const ASPECTS = [
  { id: '9x16', label: '9:16', w: 9, h: 16, hint: 'TikTok, Reels, Shorts' },
  { id: '4x5', label: '4:5', w: 4, h: 5, hint: 'Instagram and Facebook feed' },
  { id: '1x1', label: '1:1', w: 1, h: 1, hint: 'Square posts' },
  { id: '16x9', label: '16:9', w: 16, h: 9, hint: 'YouTube, LinkedIn' },
]
export const aspectById = (id) => ASPECTS.find((a) => a.id === id) || ASPECTS[0]

/** Canvas size: the short side is 720 (free) or 1080 (Pro). Always even numbers. */
export function frameSize(aspectId, hd) {
  const a = aspectById(aspectId)
  const short = hd ? 1080 : 720
  const even = (n) => Math.round(n / 2) * 2
  return a.w <= a.h ? { width: short, height: even((short * a.h) / a.w) } : { width: even((short * a.w) / a.h), height: short }
}

export const FPS = 30
/** Social apps re-compress everything, so higher bitrates only cost memory. */
export const videoBitrate = (size) => (Math.min(size.width, size.height) >= 1080 ? 6_000_000 : 3_500_000)
export const AUDIO_BITRATE = 128_000

const MR_MP4 = ['video/mp4;codecs="avc1.640028,mp4a.40.2"', 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"', 'video/mp4;codecs=avc1,mp4a.40.2', 'video/mp4;codecs=avc1', 'video/mp4']
const MR_WEBM = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
const mrOk = (t) => { try { return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t) } catch (e) { return false } }

/** H.264 profiles to try, smallest level that fits the frame first. */
function avcCodecs(size) {
  const mbs = Math.ceil(size.width / 16) * Math.ceil(size.height / 16)
  return mbs <= 3600 ? ['avc1.4d001f', 'avc1.42001f', 'avc1.640028', 'avc1.4d0028', 'avc1.42e028'] : ['avc1.640028', 'avc1.4d0028', 'avc1.42e028', 'avc1.640032']
}

async function webCodecs(size, sampleRate, vp9 = false) {
  if (typeof window === 'undefined' || !window.VideoEncoder || !window.VideoFrame || !window.AudioEncoder || !window.AudioData || !window.AudioWorkletNode) return null
  let video = null
  for (const codec of vp9 ? ['vp09.00.10.08'] : avcCodecs(size)) {
    const cfg = { codec, width: size.width, height: size.height, bitrate: videoBitrate(size), framerate: FPS, avc: { format: 'avc' } }
    try { const r = await window.VideoEncoder.isConfigSupported(cfg); if (r.supported) { video = r.config || cfg; break } } catch (e) { /* try next */ }
  }
  if (!video) return null
  video = { ...video, mux: vp9 ? 'vp9' : 'avc' }
  for (const [codec, muxCodec] of [['mp4a.40.2', 'aac'], ['opus', 'opus']]) {
    const cfg = { codec, sampleRate, numberOfChannels: 1, bitrate: AUDIO_BITRATE }
    try { const r = await window.AudioEncoder.isConfigSupported(cfg); if (r.supported) return { video, audio: { ...cfg, mux: muxCodec } } } catch (e) { /* try next */ }
  }
  return null
}

/**
 * How this browser will record. → { kind: 'wc' | 'mr', mime, ext, label, video?, audio? } or null.
 * sampleRate: the AudioContext rate the microphone will run at.
 */
export async function pickPath(size, sampleRate = 48000) {
  // Troubleshooting: localStorage "clippcut:route" = "wc" or "mr" forces one route when the browser has it.
  let force = ''
  try { force = String(JSON.parse(localStorage.getItem('clippcut:route') || '""')) } catch (e) { force = '' }
  // "wc-vp9" runs the same WebCodecs pipeline with VP9 video: only for testing in browsers without an H.264 encoder.
  if (force === 'wc-vp9') { const t = await webCodecs(size, sampleRate, true); if (t) return { kind: 'wc', ...t, mime: 'video/mp4', ext: 'mp4', label: 'MP4 (VP9, test)' } }
  const wc = await webCodecs(size, sampleRate)
  if (force === 'wc' && wc) return { kind: 'wc', ...wc, mime: 'video/mp4', ext: 'mp4', label: `MP4 (H.264 + ${wc.audio.mux === 'aac' ? 'AAC' : 'Opus'})` }
  if (force === 'mr') { const m = [...MR_MP4, ...MR_WEBM].find(mrOk); if (m) return { kind: 'mr', mime: m, ext: m.includes('mp4') ? 'mp4' : 'webm', label: m.includes('mp4') ? 'MP4' : 'WebM' } }
  if (wc && wc.audio.mux === 'aac') return { kind: 'wc', ...wc, mime: 'video/mp4', ext: 'mp4', label: 'MP4 (H.264 + AAC)' }
  const mp4 = MR_MP4.find(mrOk)
  if (mp4) return { kind: 'mr', mime: mp4, ext: 'mp4', label: 'MP4' }
  if (wc) return { kind: 'wc', ...wc, mime: 'video/mp4', ext: 'mp4', label: 'MP4 (H.264 + Opus)' }
  const webm = MR_WEBM.find(mrOk)
  if (webm) return { kind: 'mr', mime: webm, ext: 'webm', label: 'WebM' }
  return null
}

/** Can this browser record video at all? (Checked before asking for the camera.) */
export const canRecord = () => typeof window !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  && (typeof MediaRecorder !== 'undefined' || !!window.VideoEncoder) && typeof HTMLCanvasElement !== 'undefined'

/** clippcut-2026-10-12-0945-9x16.mp4 */
export function fileName(aspectId, ext, d = new Date()) {
  const p = (n) => String(n).padStart(2, '0')
  return `clippcut-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}-${aspectId}.${ext}`
}
